// longPositionTool.js — shared "Long Position" click-drag multi-drawing tool for both
// charts (Dashboard's SignalChart.js and the More Info chart in App.js). Last of the
// original 4-tool "advanced drawing tools" plan (Trend Line, Fib, Channel, Long Position).
// Interaction is the SAME 2-point click-drag as Trend Line/Fib (mousedown = entry,
// drag = risk, mouseup commits) — NOT a 3-point click sequence. v1 simplification:
// instead of independently-draggable entry/target/stop lines (TradingView's real tool),
// only entry (price1, at mousedown) and a risk distance (|price1 - price2|, from the
// drag) are captured; the take-profit target is auto-derived using a fixed 2:1
// reward:risk ratio. This keeps the interaction identical to every other tool in this
// set instead of introducing a 3rd interaction pattern for the one tool the founder
// flagged as lowest priority. Direction-agnostic: whichever way the user drags, the
// box always renders as a proper LONG (profit zone above entry, loss zone below) —
// derived from the drag's vertical DISTANCE, not its raw sign.
//
// Includes the xToTime/timeToX extrapolation fix from the start (see fibTool.js's
// header comment for the full story). type="long_position" in chart_advanced_drawings.

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchAdvancedDrawings, insertAdvancedDrawing, deleteAdvancedDrawing } from "./advancedDrawingsStore";

const TYPE = "long_position";
const REWARD_RISK = 2; // fixed 2:1 target:stop ratio — target is derived, not independently dragged

// Time↔pixel helpers that extrapolate linearly from the visible range when the native
// coordinateToTime/timeToCoordinate return null — see fibTool.js/channelTool.js's
// identical helpers for the full rationale. Duplicated rather than shared, matching this
// codebase's existing convention for these small per-tool helpers.
function xToTime(chart, x) {
  const ts = chart.timeScale();
  const t = ts.coordinateToTime(x);
  if (t != null) return t;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || xFrom === xTo) return null;
  return range.from + ((x - xFrom) / (xTo - xFrom)) * (range.to - range.from);
}
function timeToX(chart, time) {
  const ts = chart.timeScale();
  const x = ts.timeToCoordinate(time);
  if (x != null) return x;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || range.to === range.from) return null;
  return xFrom + ((time - range.from) / (range.to - range.from)) * (xTo - xFrom);
}

// Shared derivation: entry = price1 (mousedown), risk = |price1 - price2| (drag distance).
// Always renders as a long — stop below entry, target above — regardless of drag direction.
function deriveLevels(pts) {
  const entry = pts.price1;
  const risk = Math.abs(pts.price1 - pts.price2);
  const stop = entry - risk;
  const target = entry + REWARD_RISK * risk;
  return { entry, stop, target, risk };
}

export function useLongPositionTool({ chartRef, seriesRef, containerRef, userId, symbol, context }) {
  const [longMode, setLongMode] = useState(false);
  const [myPositions, setMyPositions] = useState([]); // [{id, time1, price1, time2, price2}] — DATA space
  const [draft, setDraft] = useState(null); // in-progress drag, DATA space, not yet persisted
  const [, setRangeTick] = useState(0);
  const draggingRef = useRef(false);
  const draftRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setMyPositions([]);
    if (!userId || !symbol || !context) return;
    fetchAdvancedDrawings(userId, symbol, context, TYPE)
      .then((rows) => {
        if (cancelled) return;
        setMyPositions(rows.map((r) => ({ id: r.id, ...r.points })));
      })
      .catch((e) => console.error("[chart_advanced_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = xToTime(chart, x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!longMode || !container || !userId) return;

    const handleMouseDown = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      draggingRef.current = true;
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      const next = { time1: p.time, price1: p.price, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;
      const p = pointFromEvent(e);
      if (!p) return;
      const prev = draftRef.current;
      if (!prev) return;
      const next = { ...prev, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseUp = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (!wasDragging) return;
      const finished = draftRef.current;
      draftRef.current = null;
      setDraft(null);
      if (!finished || (finished.time1 === finished.time2 && finished.price1 === finished.price2)) return;
      insertAdvancedDrawing(userId, symbol, context, TYPE, finished)
        .then((row) => setMyPositions((prev) => [...prev, { id: row.id, ...row.points }]))
        .catch((e) => console.error("[chart_advanced_drawings-insert]", e?.message || e));
      // Deliberately does NOT disarm — multi-drawing mode, same as Trend Line/Fib.
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    };
  }, [longMode, containerRef, chartRef, pointFromEvent, userId, symbol, context]);

  const deletePosition = useCallback((id) => {
    if (!userId) return;
    deleteAdvancedDrawing(userId, id)
      .then(() => setMyPositions((prev) => prev.filter((p) => p.id !== id)))
      .catch((e) => console.error("[chart_advanced_drawings-delete]", e?.message || e));
  }, [userId]);

  // WATCHDOG version (2026-07-09) — re-attaches when the chart instance appears or is
  // recreated, immediate tick on attach. See trendLineTool.js comment for why.
  useEffect(() => {
    if (myPositions.length === 0 && !draft) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, myPositions.length, draft]);

  // Derived: project each position's box to pixel space + compute entry/target/stop
  // levels, recomputed every render.
  const projectedPositions = [];
  const chart = chartRef.current;
  const series = seriesRef.current;
  if (chart && series) {
    const project = (pts, id, isDraft) => {
      const x1 = timeToX(chart, pts.time1);
      const x2 = timeToX(chart, pts.time2);
      if (x1 == null || x2 == null) return null;
      const { entry, stop, target, risk } = deriveLevels(pts);
      if (risk === 0) return null; // degenerate drag, nothing to render yet
      const yEntry = series.priceToCoordinate(entry);
      const yStop = series.priceToCoordinate(stop);
      const yTarget = series.priceToCoordinate(target);
      if (yEntry == null || yStop == null || yTarget == null) return null;
      const profitPct = ((target - entry) / entry) * 100;
      const lossPct = ((entry - stop) / entry) * 100;
      return {
        id, x1, x2, yEntry, yStop, yTarget,
        entry, stop, target, profitPct, lossPct, isDraft,
      };
    };
    myPositions.forEach((p) => {
      const proj = project(p, p.id, false);
      if (proj) projectedPositions.push(proj);
    });
    if (draft) {
      const proj = project(draft, null, true);
      if (proj) projectedPositions.push(proj);
    }
  }

  return { longMode, setLongMode, myPositions, deletePosition, projectedPositions };
}

// SVG overlay — a filled green rectangle from entry to target (profit zone), a filled
// red rectangle from entry to stop (loss zone), a solid entry line, and a small stats
// label (R:R, entry/target/stop prices + %). Green (#4ade80) reuses the app's existing
// Support color; red (#f87171) reuses the app's existing loss/negative color — both
// semantically correct here (universal profit/loss convention) rather than inventing
// new colors, unlike Trend Line/Channel/Fib/Ray which each needed a distinct new hue.
// pointerEvents: "none" on the SVG root — see measureTool.js's MeasureOverlay comment
// for why that's critical. The delete "×" badge is the one exception: it gets its own
// pointerEvents: "auto" so it's clickable despite the root being click-through, same
// pattern as MeasureOverlay's own onClear button. onDelete is optional so drafts (which
// have no id yet) simply don't render one.
export function LongPositionOverlay({ positions, onDelete }) {
  if (!positions || positions.length === 0) return null;
  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      {positions.map((p, i) => {
        const minX = Math.min(p.x1, p.x2);
        const maxX = Math.max(p.x1, p.x2);
        const width = Math.max(1, maxX - minX);
        const topY = Math.min(p.yTarget, p.yEntry, p.yStop);
        return (
          <g key={p.id ?? `draft-${i}`} opacity={p.isDraft ? 0.6 : 1}>
            <rect
              x={minX} y={Math.min(p.yTarget, p.yEntry)}
              width={width} height={Math.max(1, Math.abs(p.yEntry - p.yTarget))}
              fill="#4ade80" fillOpacity="0.18"
            />
            <rect
              x={minX} y={Math.min(p.yEntry, p.yStop)}
              width={width} height={Math.max(1, Math.abs(p.yStop - p.yEntry))}
              fill="#f87171" fillOpacity="0.18"
            />
            <line x1={minX} y1={p.yTarget} x2={maxX} y2={p.yTarget} stroke="#4ade80" strokeWidth="1" />
            <line x1={minX} y1={p.yEntry} x2={maxX} y2={p.yEntry} stroke="#e5e7eb" strokeWidth="1.25" />
            <line x1={minX} y1={p.yStop} x2={maxX} y2={p.yStop} stroke="#f87171" strokeWidth="1" />
            <text x={maxX + 4} y={p.yTarget + 3} fill="#4ade80" fontSize="10" fontFamily="system-ui">
              Target {p.target.toLocaleString(undefined, { maximumFractionDigits: 2 })} (+{p.profitPct.toFixed(1)}%)
            </text>
            <text x={maxX + 4} y={p.yEntry + 3} fill="#e5e7eb" fontSize="10" fontFamily="system-ui">
              Entry {p.entry.toLocaleString(undefined, { maximumFractionDigits: 2 })} · R:R 1:{REWARD_RISK}
            </text>
            <text x={maxX + 4} y={p.yStop + 3} fill="#f87171" fontSize="10" fontFamily="system-ui">
              Stop {p.stop.toLocaleString(undefined, { maximumFractionDigits: 2 })} (-{p.lossPct.toFixed(1)}%)
            </text>
            {!p.isDraft && onDelete && (
              <g
                transform={`translate(${minX + 4}, ${topY - 16})`}
                style={{ cursor: "pointer", pointerEvents: "auto" }}
                onClick={() => onDelete(p.id)}
              >
                <circle cx="8" cy="8" r="8" fill="rgba(17,17,17,0.9)" stroke="#888" strokeWidth="1" />
                <text x="8" y="11" fill="#ccc" fontSize="10" fontFamily="system-ui" textAnchor="middle">×</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
